import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
    Tabs,
    Card,
    Form,
    Input,
    Row,
    Col,
    Radio,
    Table,
    Button,
    Checkbox,
    DatePicker
} from "antd";

import {
    User,
    Activity,
    ClipboardList,
    Sparkles,
    Save,
    Printer
} from "lucide-react";

import { useState } from "react";
import dayjs from "dayjs";
import Layout from '../../Layout'

const { TextArea } = Input;

interface FaceRecord {
    key: number;
    name: string;
}

export default function ThreadLiftReception() {
  const { t } = useTranslation();

    const [form] = Form.useForm();

    const faceList: FaceRecord[] = [
        { key: 1, name: t('thread_lift.face_pathology') },
        { key: 2, name: t('thread_lift.face_skin') },
        { key: 3, name: t('thread_lift.face_lip_cheek') },
        { key: 4, name: t('thread_lift.face_mouth_corner') },
        { key: 5, name: t('thread_lift.face_mid_volume') },
        { key: 6, name: t('thread_lift.face_crow_feet') },
        { key: 7, name: t('thread_lift.face_forehead_wrinkle') },
        { key: 8, name: t('thread_lift.face_frown') },
        { key: 9, name: t('thread_lift.face_nasolabial') },
        { key: 10, name: t('thread_lift.face_nose_wrinkle') },
        { key: 11, name: t('thread_lift.face_mouth_wrinkle') },
        { key: 12, name: t('thread_lift.face_neck_wrinkle') },
        { key: 13, name: t('thread_lift.face_forehead_line') },
        { key: 14, name: t('thread_lift.face_eye_hollow') },
        { key: 15, name: t('thread_lift.face_temple_hollow') },
        { key: 16, name: t('thread_lift.face_cheek_hollow') },
        { key: 17, name: t('thread_lift.face_sagging_cheek') },
        { key: 18, name: t('thread_lift.face_chubby_cheek') },
        { key: 19, name: t('thread_lift.face_double_chin') },
        { key: 20, name: t('thread_lift.face_cheek_fat') },
        { key: 21, name: t('thread_lift.face_brow_drop') },
        { key: 22, name: t('thread_lift.face_ptosis') },
        { key: 23, name: t('thread_lift.face_other') }
    ];

    const [state, setState] = useState<Record<number, string>>(
        faceList.reduce((acc, item) => {
            acc[item.key] = "no";
            return acc;
        }, {} as Record<number, string>)
    );

    const columns = [
        {
            title: t('thread_lift.col_status'),
            dataIndex: "name"
        },
        {
            title: t('thread_lift.col_yes'),
            align: "center" as const,
            width: 80,
            render: (_: any, record: FaceRecord) => (
                <Radio
                    checked={state[record.key] === "yes"}
                    onChange={() =>
                        setState(prev => ({
                            ...prev,
                            [record.key]: "yes"
                        }))
                    }
                />
            )
        },
        {
            title: t('thread_lift.col_no'),
            align: "center" as const,
            width: 80,
            render: (_: any, record: FaceRecord) => (
                <Radio
                    checked={state[record.key] === "no"}
                    onChange={() =>
                        setState(prev => ({
                            ...prev,
                            [record.key]: "no"
                        }))
                    }
                />
            )
        },
        {
            title: t('thread_lift.col_explain'),
            render: (_: any, record: FaceRecord) => (
                <Input disabled={state[record.key] !== "yes"} />
            )
        }
    ];

    const items = [

        {
            key: "1",
            label: (
                <span className="flex items-center gap-2">
                    <User size={16} />
                    {t('thread_lift.tab_info')}
                </span>
            ),

            children: (
                <Card className="shadow-sm">

                    <Form layout="vertical" form={form} className="border-color-1f1f1f">

                        {/* THÔNG TIN CÁ NHÂN */}

                        <div className="mb-6">

                            <div className="font-semibold mb-3 text-000000e0">
                                {t('thread_lift.personal_info')}
                            </div>

                            <div className="border rounded-lg p-4 border-color-1f1f1f">

                                <Row gutter={16}>

                                    <Col xs={24} md={12}>
                                        <Form.Item label={t('thread_lift.full_name')}>
                                            <Input />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item label={t('thread_lift.gender')}>
                                            <Radio.Group>
                                                <Radio value="male">{t('thread_lift.male')}</Radio>
                                                <Radio value="female">{t('thread_lift.female')}</Radio>
                                            </Radio.Group>
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item label={t('thread_lift.dob')}>
                                            <DatePicker className="w-full" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item label={t('thread_lift.phone')}>
                                            <Input />
                                        </Form.Item>
                                    </Col>

                                    <Col span={24}>
                                        <Form.Item label={t('thread_lift.email')}>
                                            <Input />
                                        </Form.Item>
                                    </Col>

                                    <Col span={24}>
                                        <Form.Item label={t('thread_lift.address')}>
                                            <Input />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item label={t('thread_lift.current_job')}>
                                            <Input />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item label={t('thread_lift.info_source')}>
                                            <Input />
                                        </Form.Item>
                                    </Col>

                                </Row>

                            </div>

                        </div>

                        {/* TÌNH TRẠNG & NHU CẦU */}

                        <div>
                            <div className="font-semibold mb-3 text-000000e0">
                                {t('thread_lift.status_needs')}
                            </div>
                            <div className="border rounded-lg p-4 border-color-1f1f1f">
                                <Row gutter={24}>
                                    <Col span={24}>
                                        <Form.Item label={t('thread_lift.current_status_customer')}>
                                            <TextArea rows={3} />
                                        </Form.Item>
                                    </Col>

                                    <Col span={24}>
                                        <Form.Item label={t('thread_lift.improvement_needs')}>
                                            <TextArea rows={3} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>
                        </div>

                    </Form>

                    <div className="flex justify-end gap-3 mt-4">
                        <Button icon={<Printer size={16} />}>{t('thread_lift.print')}</Button>
                        <Button type="primary" icon={<Save size={16} />}>
                            {t('thread_lift.save')}
                        </Button>
                    </div>

                </Card>
            )
        },

        {
            key: "2",
            label: (
                <span className="flex items-center gap-2">
                    <Activity size={16} />
                    {t('thread_lift.tab_face')}
                </span>
            ),

            children: (
                <Card>

                    <div className="bg-purple-50 text-purple-600 text-sm p-3 rounded mb-4">
                        {t('thread_lift.face_note')}
                    </div>

                    <Table
                        columns={columns}
                        dataSource={faceList}
                        pagination={false}
                        className="custom-ant-table"
                        scroll={{ x: "max-content" }}
                    />

                    <div className="flex justify-end gap-3 mt-4">
                        <Button icon={<Printer size={16} />}>{t('thread_lift.print')}</Button>
                        <Button type="primary" icon={<Save size={16} />}>
                            {t('thread_lift.save')}
                        </Button>
                    </div>

                </Card>
            )
        },

        {
            key: "3",
            label: (
                <span className="flex items-center gap-2">
                    <ClipboardList size={16} />
                    {t('thread_lift.tab_doctor')}
                </span>
            ),

            children: (
                <Card>

                    {/* INFO BOX */}
                    <div className="bg-purple-50 text-purple-600 text-sm p-3 rounded-md mb-5">
                        {t('thread_lift.doctor_note')}
                    </div>

                    <Form layout="vertical">

                        {/* ROW 1 */}
                        <Row gutter={16}>

                            <Col xs={24} md={12}>
                                <Form.Item label={t('thread_lift.current_status')}>
                                    <Input.TextArea rows={4} />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item label={t('thread_lift.customer_needs')}>
                                    <Input.TextArea rows={4} />
                                </Form.Item>
                            </Col>

                        </Row>

                        {/* Ý KIẾN BÁC SĨ */}
                        <Form.Item label={t('thread_lift.doctor_opinion')}>
                            <Input.TextArea rows={3} />
                        </Form.Item>

                        {/* PHƯƠNG PHÁP */}
                        <Form.Item label={t('thread_lift.treatment_method')}>
                            <Input.TextArea rows={3} />
                        </Form.Item>

                    </Form>

                    {/* CARD CHỈ ĐỊNH */}
                    <div className="border rounded-lg p-4 mb-4 border-color-1f1f1f">

                        <div className="font-medium mb-3 text-000000e0">
                            {t('thread_lift.thread_types')}
                        </div>

                        <Row gutter={[16, 12]}>

                            <Col xs={24} md={8}><Checkbox>Chỉ Nano – Whale tupe</Checkbox></Col>
                            <Col xs={24} md={8}><Checkbox>Chỉ Nano – Shark type</Checkbox></Col>
                            <Col xs={24} md={8}><Checkbox>Anchor FIX - 360</Checkbox></Col>

                            <Col xs={24} md={8}><Checkbox>Chỉ Matrix</Checkbox></Col>
                            <Col xs={24} md={8}><Checkbox>Chỉ Craven S-Lift</Checkbox></Col>
                            <Col xs={24} md={8}><Checkbox>Chỉ Scew Super PCL</Checkbox></Col>

                            <Col xs={24} md={8}><Checkbox>Chỉ Scew - Moisture</Checkbox></Col>
                            <Col xs={24} md={8}><Checkbox>Chỉ Scew Lipo</Checkbox></Col>
                            <Col xs={24} md={8}><Checkbox>Chỉ Double S-Press Cog</Checkbox></Col>

                            <Col xs={24} md={8}><Checkbox>Temple - Lift</Checkbox></Col>

                        </Row>

                    </div>

                    {/* CHI PHÍ */}
                    <Form layout="vertical">

                        <Form.Item label={t('thread_lift.treatment_cost')}>
                            <Input prefix="VNĐ" />
                        </Form.Item>

                    </Form>

                    {/* BUTTON */}
                    <div className="flex justify-end gap-3">

                        <Button icon={<Printer size={16} />}>
                            {t('thread_lift.print')}
                        </Button>

                        <Button type="primary" icon={<Save size={16} />}>
                            {t('thread_lift.save')}
                        </Button>

                    </div>

                </Card>
            )
        }

    ];

    return (
        <Layout>
            <Helmet>
                <title>{t('page.thread_lift')} | Khothi</title>
            </Helmet>

            <div className="flex justify-between mb-6">

                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-600 text-white flex items-center justify-center rounded-lg">
                        <Sparkles size={18} />
                    </div>

                    <div>
                        <h1 className="font-semibold text-lg text-000000e0">
                            {t('thread_lift.page_title')}
                        </h1>
                        <p className="text-sm text-gray-500">
                            {t('thread_lift.page_subtitle')}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <DatePicker
                        defaultValue={dayjs()}
                        format="DD/MM/YYYY"
                        className="min-w-[160px]"
                    />
                </div>

            </div>

            <Tabs items={items} />

        </Layout>
    );
}